import { useGetUserPermissionQuery } from 'apis/AVPPermission/getPermissionAllAction'
import { AVP_SERVICE_ACTION_TRACKER, AVP_SERVICE_ASSURANCE_SCORECARD, AVP_SERVICE_BARRIER_MANAGEMENT, AVP_SERVICE_PROCESS_INTEGRITY } from 'constants/avp_permissions'
import { useParams } from 'react-router-dom'
import { isEmpty, isNotEmpty } from 'utils/data_manipulator'

/**
 * Interface defining the structure for permission checks.
 * @property type - The category of permission (e.g., 'list', 'read', 'write').
 * @property action - The specific action name(s) to validate.
 */

type IUsePermission = {
  type: ('list' | 'read' | 'write')[] | 'list' | 'read' | 'write'
  action: string | string[]
}

/**
 * Array of permission action names to be fetched from the backend.
 * These actions correspond to specific features or modules within the application.
 */
const permissionActionFetch = [ //partial list of actions to fetch for permission check, can be extended as needed
  'GetModuleAccess',
  'CreateUpdateKPIGroup',
  'CreateUpdateProjectThreshold',
  'BatchUpdateActions',
  'BatchDownloadActions',
  'FormObservationAction',
  'AssessmentFormAction',
  'InsightGraphs',
  'Dashboard',
  'SyncActions',
]

/**
 * Custom hook to manage and check user permissions for projects and subprojects (assets).
 * It fetches the permission data based on the current project ID and provides
 * utility functions to check for specific permissions at different levels.
 */
export const useModulePermission = () => {
  const { project_id, asset_id } = useParams()
  const { data, isLoading, isError } = useGetUserPermissionQuery({
    projectId: project_id!,
    partialActionNames: permissionActionFetch,
  })

  /**
   * Helper function to check if any of the requested actions exist in the user's permission list.
   * @param userPermissions - Array of actions the user has permission for.
   * @param actions - The action(s) needed.
   * @returns True if the user has permission, false otherwise.
   */

  const checkModuleAccess = (assetId?: string | number): boolean => {
    // to check if user has module access by looking for 'GetModuleAccess' in their permissions
    // If assetId is provided, check subproject permissions; otherwise, check project permissions
    const readActions = assetId ? data?.subprojects?.[assetId]?.read || [] : data?.project?.read || []
    return readActions.some((act) => act === 'GetModuleAccess' || act.endsWith(':GetModuleAccess'))
  }

  const checkPermission = (userPermissions: string[], actions: string | string[]): boolean => {
    const _actions = Array.isArray(actions) ? actions : [actions]
    const actionsSet = new Set(_actions) // convert array to set for efficient lookup
    return userPermissions.some((act) => actionsSet.has(act))
  }

  const filterActionsByType = (type: IUsePermission['type'], data: any): string[] => {
    if (typeof type === 'string') {
      // single type
      return data[type] || []
    } else {
      // multiple types
      return type.flatMap((t) => data[t] || [])
    }
  }

  /**
   * Checks if the user has the required permission at the Project level.
   */
  const checkProjectPermission = (props: IUsePermission): boolean | null => {
    // to check permission in project
    const { action, type } = props
    if (!data || !data.project) return null
    const userPermissions = filterActionsByType(type, data.project)
    if (checkModuleAccess() === false) {
      return false
    }
    return checkPermission(userPermissions, action)
  }

  /**
   * Checks if the user has the required permission for a specific Subproject (Asset).
   */
  const checkSubprojectPermission = (
    props: IUsePermission & { assetId: string | number }
  ): boolean | null => {
    // to check permission in subproject
    const { action, type, assetId } = props
    if (!data || !data.subprojects) return null
    const subproject = data.subprojects[assetId]
    if (!subproject) return null
    const userPermissions = filterActionsByType(type, subproject)
    if (checkModuleAccess(assetId) === false || checkModuleAccess() === false) {
      return false
    }
    return checkPermission(userPermissions, action)
  }

  /**
   * Checks if the user has the required permission in ANY of the available subprojects.
   * Useful for aggregation views where access to at least one asset is sufficient.
   */
  const checkInAnySubprojectPermission = (props: IUsePermission): boolean | null => {
    // to check permission in any subproject
    const { action, type } = props
    if (!data || !data.subprojects) return null
    const userPermissions = Object.values(data.subprojects)?.map((subproject) =>
      filterActionsByType(type, subproject || {})
    )
    const allPermissions = userPermissions.flat()
    if (checkModuleAccess() === false) {
      return false
    }
    return checkPermission(allPermissions, action)
  }

  /**
   * Context-aware permission check.
   * If an assetId is provided, checks permission for that subproject.
   * Otherwise, checks permission at the project level.
   */
  const checkInAllFacilitiesPermission = (
    props: IUsePermission & { assetId?: string | number }
  ): boolean | null => {
    // to check permission in both project and subprojects
    const { action, type, assetId } = props
    if (assetId) {
      return checkSubprojectPermission({ action, type, assetId })
    }
    return checkProjectPermission({ action, type })
  }

  const checkProjectSubprojectPermission = (props: IUsePermission & { assetId?: string | number }): boolean | null => {
    // to check permission in both project and subprojects, with assetId optional
    const { action, type, assetId } = props
    if (assetId) {
      const subprojectPermission = checkSubprojectPermission({ action, type, assetId })
      if (isNotEmpty(subprojectPermission) && subprojectPermission === true) {
        return subprojectPermission
      }
    }
    return checkProjectPermission({ action, type })
  }

  /**
   * Checks permission for one or more modules by expanding `action` across provided module names.
   * Example: modules=[BM, PSE], action='BatchDownload' -> checks ['BM:BatchDownload','PSE:BatchDownload'].
   */
  const checkModulesPermission = (
    props: { type: IUsePermission['type']; action: string | string[]; modules: string[] | string ; assetId?: string | number }
  ): boolean | null => {
    const { type, action, modules, assetId } = props
    const _modules = (typeof modules === 'string' && modules === AVP_SERVICE_ACTION_TRACKER) ? [
        AVP_SERVICE_BARRIER_MANAGEMENT,
        AVP_SERVICE_PROCESS_INTEGRITY,
        AVP_SERVICE_ASSURANCE_SCORECARD,
        AVP_SERVICE_ACTION_TRACKER
      ] : [modules] as string[] // ensure modules is an array
    
    const actionsArray = Array.isArray(action) ? action : [action]
    const expandedActions = _modules.flatMap((m) => actionsArray.map((a) => `${m}:${a}`))
    return checkProjectSubprojectPermission({ type, action: expandedActions, assetId })
  }

  return {
    checkProjectPermission,
    checkSubprojectPermission,
    checkInAnySubprojectPermission,
    checkInAllFacilitiesPermission,
    checkProjectSubprojectPermission,
    checkModulesPermission,
    permissions: data,
    loading: isLoading,
  }
}
